/**
 * Page Builder Index - Canonical Page Registry
 * 
 * The Page Builder is the SINGLE SOURCE OF TRUTH for every page in Trap Comix.
 * This index shows ALL registered pages - no exceptions, no hardcoded layouts.
 * 
 * Pages are fetched from the page_registry API, not hardcoded.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layout, Globe, Home, Rocket, Loader2, Plus,
  ExternalLink, Clock, AlertCircle, ArrowRight, Palette,
  Search, Filter, Grid, List, Eye, EyeOff,
  BookOpen, Users, Image as ImageIcon, Award, Settings,
  Compass, FileText, Lock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { DesignTokensEditor } from '../../components/page-builder';
import NewPageModal from '../../components/page-builder/wysiwyg/NewPageModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon map for page types
const PAGE_TYPE_ICONS = {
  HOMEPAGE: Home,
  LANDING: Rocket,
  DISCOVERY: Compass,
  BROWSE: Grid,
  COMIC_SERIES: BookOpen,
  NOVEL_SERIES: BookOpen,
  CHAPTER: FileText,
  READER_VERTICAL: BookOpen,
  READER_HORIZONTAL: BookOpen,
  READER_PAGE_BY_PAGE: BookOpen,
  CREATOR_PROFILE: Users,
  CREATOR_DASHBOARD: Users,
  GALLERY: ImageIcon,
  LEADERBOARD: Award,
  ABOUT: FileText,
  FAQ: FileText,
  PRIVACY: FileText,
  TERMS: FileText,
  PAGE_BUILDER_INDEX: Layout,
  ADMIN_DASHBOARD: Settings,
  SITE_MANAGER: Settings,
  CUSTOM: Globe,
};

const getPageIcon = (pageType) => {
  return PAGE_TYPE_ICONS[pageType] || Globe;
};

const PageBuilderIndex = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // State
  const [pages, setPages] = useState([]);
  const [pageTypes, setPageTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDesignTokens, setShowDesignTokens] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVisibility, setFilterVisibility] = useState('all'); // all, public, admin-only
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [showNewPageModal, setShowNewPageModal] = useState(false);

  // Listen for inline "New Page" button events from child components
  useEffect(() => {
    const handler = () => setShowNewPageModal(true);
    window.addEventListener('open-new-page-modal', handler);
    return () => window.removeEventListener('open-new-page-modal', handler);
  }, []);

  // Auth check
  useEffect(() => {
    if (authLoading) return;
    
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    let isAdminUser = user?.role === 'admin' || user?.is_admin;
    
    if (!isAdminUser && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        isAdminUser = parsedUser?.role === 'admin' || parsedUser?.is_admin;
      } catch (e) {}
    }
    
    if (!token || !isAdminUser) {
      navigate('/');
      return;
    }

    fetchPageRegistry();
  }, [user, authLoading, navigate]);

  const fetchPageRegistry = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/page-registry`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPages(response.data.pages || []);
      setPageTypes(response.data.page_types || {});
    } catch (error) {
      console.error('Error fetching page registry:', error);
      toast.error('Failed to load page registry');
    } finally {
      setLoading(false);
    }
  };

  // Filtered pages
  const filteredPages = useMemo(() => {
    return pages.filter(page => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!page.title.toLowerCase().includes(query) &&
            !page.slug.toLowerCase().includes(query) &&
            !page.pageType.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Visibility filter
      if (filterVisibility !== 'all' && page.visibility !== filterVisibility) {
        return false;
      }
      
      return true;
    });
  }, [pages, searchQuery, filterVisibility]);

  // Group pages by visibility
  const publicPages = filteredPages.filter(p => p.visibility === 'public');
  const adminPages = filteredPages.filter(p => p.visibility === 'admin-only');

  // Get editor URL for a page
  const getEditorUrl = (pageId, pageType) => {
    // Special cases
    if (pageId === 'homepage') return '/admin/page-builder/homepage';
    if (pageId === 'landing_early_access') return '/admin/page-builder/landing';
    
    // Dynamic pages
    if (['COMIC_SERIES', 'NOVEL_SERIES', 'CHAPTER', 'CREATOR_PROFILE'].includes(pageType)) {
      return `/admin/page-builder/template/${pageId}`;
    }
    
    // Default
    return `/admin/page-builder/${pageId}`;
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const PageCard = ({ page }) => {
    const Icon = getPageIcon(page.pageType);
    const editorUrl = getEditorUrl(page.pageId, page.pageType);
    const hasDefinition = page.has_definition || page.has_draft || page.has_published;
    
    return (
      <Card 
        className={`
          bg-zinc-900 border-zinc-800 transition-all cursor-pointer group
          ${hasDefinition 
            ? 'hover:border-orange-500/50' 
            : 'hover:border-yellow-500/50 opacity-75'
          }
        `}
        onClick={() => navigate(editorUrl)}
        data-testid={`page-card-${page.pageId}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className={`
              p-3 rounded-lg group-hover:scale-105 transition-transform
              ${page.visibility === 'admin-only' 
                ? 'bg-purple-500/10' 
                : 'bg-orange-500/10 group-hover:bg-orange-500/20'
              }
            `}>
              <Icon className={`w-5 h-5 ${
                page.visibility === 'admin-only' ? 'text-purple-400' : 'text-orange-500'
              }`} />
            </div>
            <div className="flex gap-1.5 flex-wrap justify-end">
              {page.is_system && (
                <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px]">
                  System
                </Badge>
              )}
              {page.visibility === 'admin-only' && (
                <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-[10px]">
                  <Lock className="w-2.5 h-2.5 mr-1" />
                  Admin
                </Badge>
              )}
              {page.has_draft && !page.has_published && (
                <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-[10px]">
                  Draft
                </Badge>
              )}
              {page.has_published && (
                <Badge variant="outline" className="border-green-500/50 text-green-400 text-[10px]">
                  Live
                </Badge>
              )}
              {!hasDefinition && (
                <Badge variant="outline" className="border-zinc-600 text-zinc-500 text-[10px]">
                  No Definition
                </Badge>
              )}
            </div>
          </div>
          <CardTitle className="text-white text-base mt-3 flex items-center gap-2">
            {page.title}
            <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-orange-500 transition-colors" />
          </CardTitle>
          <CardDescription className="text-zinc-500 text-sm line-clamp-2">
            {page.description || pageTypes[page.pageType]?.description || `${page.pageType} page`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Globe className="w-3.5 h-3.5" />
              <span className="truncate max-w-[120px]">{page.slug}</span>
            </div>
            {page.visibility === 'public' && (
              <a
                href={page.slug}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-orange-400 hover:text-orange-300"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>View</span>
              </a>
            )}
          </div>
          
          {page.updatedAt && (
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-600 mt-3 pt-2 border-t border-zinc-800/50">
              <Clock className="w-3 h-3" />
              <span>Updated {new Date(page.updatedAt).toLocaleDateString()}</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Layout className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Page Builder</h1>
                <p className="text-sm text-zinc-500">Canonical authority for all pages</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowDesignTokens(true)}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Palette className="w-4 h-4 mr-2" />
                Tokens
              </Button>
              <Button
                onClick={() => setShowNewPageModal(true)}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="new-page-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Page
              </Button>
            </div>
          </div>
          
          {/* Search and filters */}
          <div className="flex items-center gap-3 mt-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search pages..."
                className="pl-9 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            
            <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-700">
              <button
                onClick={() => setFilterVisibility('all')}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  filterVisibility === 'all' 
                    ? 'bg-zinc-700 text-white' 
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterVisibility('public')}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  filterVisibility === 'public' 
                    ? 'bg-zinc-700 text-white' 
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Public
              </button>
              <button
                onClick={() => setFilterVisibility('admin-only')}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  filterVisibility === 'admin-only' 
                    ? 'bg-zinc-700 text-white' 
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Admin
              </button>
            </div>
            
            <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-700">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{pages.length}</div>
            <div className="text-xs text-zinc-500">Total Pages</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">
              {pages.filter(p => p.has_published).length}
            </div>
            <div className="text-xs text-zinc-500">Published</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {pages.filter(p => p.has_draft && !p.has_published).length}
            </div>
            <div className="text-xs text-zinc-500">Drafts</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-zinc-500">
              {pages.filter(p => !p.has_definition && !p.has_draft && !p.has_published).length}
            </div>
            <div className="text-xs text-zinc-500">No Definition</div>
          </div>
        </div>

        {/* Public Pages */}
        {publicPages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Public Pages ({publicPages.length})
            </h2>
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
                : 'space-y-2'
            }>
              {publicPages.map(page => (
                <PageCard key={page.pageId} page={page} />
              ))}
            </div>
          </div>
        )}

        {/* Admin Pages */}
        {adminPages.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-zinc-400 mb-4 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Admin Pages ({adminPages.length})
            </h2>
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
                : 'space-y-2'
            }>
              {adminPages.map(page => (
                <PageCard key={page.pageId} page={page} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredPages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-zinc-500 mb-2">No pages found</div>
            <div className="text-zinc-600 text-sm">
              {searchQuery ? 'Try a different search term' : 'Page registry is empty'}
            </div>
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-zinc-900/50 border-zinc-800 mt-8">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-500/10 rounded-lg flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-medium mb-1">Canonical Page Authority</h3>
                <p className="text-sm text-zinc-400">
                  The Page Builder is the single source of truth for every page in Trap Comix.
                  All pages must be registered here. If a page is not in this registry, it should not render.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Design Tokens Editor Modal */}
      <DesignTokensEditor
        open={showDesignTokens}
        onClose={() => setShowDesignTokens(false)}
      />
      
      {/* New Page Modal */}
      <NewPageModal
        open={showNewPageModal}
        onClose={() => setShowNewPageModal(false)}
      />
    </div>
  );
};

export default PageBuilderIndex;
